import { getCurrentTime } from "./generalUtils"

/**
 * Returns the end time of a patient
 * @param {Object} patient - The patient object
 * @returns {number} The end time of the patient in minutes since midnight
 */
export function getEndTime(patient, sandbox, isTomorrowMode) {

	if (sandbox === undefined) {
		throw new Error("sandbox is undefined")
	}

	if (patient?.actualEndTime) {
		return patient.actualEndTime
	}

	const startTime = getStartTime(patient, sandbox, isTomorrowMode)
	let predictedEndTime = null

	if (startTime) {
		predictedEndTime = startTime + patient?.length
	}

	if (predictedEndTime < getCurrentTime(sandbox, isTomorrowMode)) {
		// predicted end time was within the last 60 minutes, so we should use the current time in case
		// code for snapback: && predictedEndTime >= getCurrentTime(sandbox, isTomorrowMode) - 60
		return getCurrentTime(sandbox, isTomorrowMode)
	}

	return predictedEndTime
}

export function getStartTime(patient, sandbox, isTomorrowMode) {

	if (sandbox === undefined) {
		throw new Error("sandbox is undefined")
	}

	const currentTime = getCurrentTime(sandbox, isTomorrowMode)

	if (patient?.actualStartTime) {
		return patient.actualStartTime
	}

	if (patient?.scheduledStartTime > currentTime) {
		return patient.scheduledStartTime
	}

	if (currentTime - 60 <= patient?.scheduledStartTime) {
		return patient?.scheduledStartTime
	}

	return patient?.scheduledStartTime
}

export function getDurationTime(patient, sandbox=false, isTomorrowMode=false) {
	return getEndTime(patient, sandbox, isTomorrowMode) - getStartTime(patient, sandbox, isTomorrowMode)
		? getEndTime(patient, sandbox, isTomorrowMode) - getStartTime(patient, sandbox, isTomorrowMode)
		: null
}

/**
 * Checks if two ranges overlap
 * @param {[number, number]} range1 - The first range [start, end]
 * @param {[number, number]} range2 - The second range [start, end]
 * @returns {boolean} True if the ranges overlap, false otherwise
 */
export function rangesOverlap(range1, range2) {
	const [start1, end1] = range1
	const [start2, end2] = range2

	return start1 < end2 && end1 > start2
}

/**
 * Checks if the start of one range is within the other range
 * @param {[number, number]} range1 - The first range [start, end]
 * @param {[number, number]} range2 - The second range [start, end]
 * @returns {boolean} True if the start of range1 is within range2, false otherwise
 */
export function startOneWithinOther(range1, range2) {
	const [start1] = range1
	const [start2, end2] = range2

	return start1 < end2 && start1 >= start2
}

/**
 * Checks if two patients overlap
 * @param {Patient} patient1 - The first patient
 * @param {Patient} patient2 - The second patient
 * @returns {boolean} True if the patients overlap, false otherwise
 */
export function hasOverlap(patient1, patient2, sandbox, isTomorrowMode) {
	const range1 = [startTimeAllCases(patient1, sandbox, isTomorrowMode), endTimeAllCases(patient1, sandbox, isTomorrowMode)]
	const range2 = [startTimeAllCases(patient2, sandbox, isTomorrowMode), endTimeAllCases(patient2, sandbox, isTomorrowMode)]
	return rangesOverlap(range1, range2)
}

/**
 * Checks if a patient is currently real time completed
 * @param {Patient} patient - The patient
 * @param {boolean} sandbox - Whether the sandbox is enabled
 * @returns {boolean} True if the patient is currently real time completed, false otherwise
 */
export function currentlyRealTimeCompleted(patient) {
	return patient?.actualEndTime && !confirmedCancellationNoShow(patient)
}

export function currentlyRealTimeOngoing(patient) {
	return (
		patient?.actualStartTime && !patient?.actualEndTime && !confirmedCancellationNoShow(patient)
	)
}

export function currentlyRealTimeFuture(patient, sandbox, isTomorrowMode) {
	const currentTime = getCurrentTime(sandbox, isTomorrowMode)
	return (
		!patient?.actualStartTime &&
		patient?.scheduledStartTime >= currentTime - 60 &&
		(patient?.internalStatus === "Scheduled" || patient?.internalStatus === "Add on") &&
		!confirmedCancellationNoShow(patient)
	)
}

export function currentlyRealTimeFuture30MinutesStarting(patient, sandbox, isTomorrowMode) {
	const currentTime = getCurrentTime(sandbox, isTomorrowMode)
	return (
		!patient?.actualStartTime &&
		patient?.scheduledStartTime >= currentTime &&
		patient?.scheduledStartTime <= currentTime + 30 &&
		!confirmedCancellationNoShow(patient)
	)
}

export function currentlyRealTimeFuture30MinutesEnding(patient, sandbox, isTomorrowMode) {
	const currentTime = getCurrentTime(sandbox, isTomorrowMode)
	return (
		patient?.actualStartTime &&
		getEndTime(patient, sandbox, isTomorrowMode) >= currentTime &&
		getEndTime(patient, sandbox, isTomorrowMode) <= currentTime + 30 &&
		!confirmedCancellationNoShow(patient)
	)
}

export function currentlyScheduledCompleted(patient, sandbox, isTomorrowMode) {
	return patient?.scheduledEndTime <= getCurrentTime(sandbox, isTomorrowMode) && !confirmedCancellationNoShow(patient)
}

export function currentlyScheduledOngoing(patient, sandbox, isTomorrowMode) {
	const currentTime = getCurrentTime(sandbox, isTomorrowMode)
	return (
		currentTime >= patient?.scheduledStartTime &&
		currentTime <= patient?.scheduledEndTime &&
		!confirmedCancellationNoShow(patient)
	)
}

export function currentlyScheduledFuture30MinutesStarting(patient, sandbox, isTomorrowMode) {
	const currentTime = getCurrentTime(sandbox, isTomorrowMode)
	return (
		patient?.scheduledStartTime >= currentTime &&
		patient?.scheduledStartTime <= currentTime + 30 &&
		!confirmedCancellationNoShow(patient)
	)
}

export function currentlyScheduledFuture30MinutesEnding(patient, sandbox, isTomorrowMode) {
	const currentTime = getCurrentTime(sandbox, isTomorrowMode)
	return (
		patient?.scheduledEndTime >= currentTime &&
		patient?.scheduledEndTime <= currentTime + 30 &&
		!confirmedCancellationNoShow(patient)
	)
}

export function currentlyScheduledFuture(patient, sandbox, isTomorrowMode) {
	const currentTime = getCurrentTime(sandbox, isTomorrowMode)
	return patient?.scheduledStartTime >= currentTime && !confirmedCancellationNoShow(patient)
}

export function startTimeAllCases(patient, sandbox, isTomorrowMode) {
	return sandbox || isTomorrowMode ? patient?.scheduledStartTime : getStartTime(patient, sandbox, isTomorrowMode)
}

export function endTimeAllCases(patient, sandbox, isTomorrowMode) {
	return sandbox || isTomorrowMode ? patient?.scheduledEndTime : getEndTime(patient, sandbox, isTomorrowMode)
}

/**
 * Checks if a patient is a cancellation or no show
 * @param {Patient} patient - The patient
 * @returns {boolean} True if the patient is a cancellation or no show, false otherwise
 */
export function confirmedCancellationNoShow(patient) {
	return patient.internalStatus === "No Show" || patient.internalStatus === "Cancellation"
}

/**
 * Returns the cancelled or no show patients that haven't been shown to the user yet
 * @param {Patient[]} patientSchedule - The patient schedule
 * @returns {Patient[]} The cancelled or no show patients that haven't been shown yet
 */
export function getCancelledNoShowPatientsNotShown(patientSchedule) {
	return patientSchedule.filter(patient => 
		confirmedCancellationNoShow(patient) && !patient.shownCancelledPatient
	)
}

/**
 * Returns the patients who are confirmed cancellation or no show
 * @param {*} patientSchedule 
 * @returns The patients who are confirmed cancellationNoShow
 */
export function getCancelledNoShowPatients(patientSchedule) {
	return patientSchedule.filter(patient => confirmedCancellationNoShow(patient))
}

export function confirmedCompletedAllCases(patient, sandbox, isTomorrowMode) {
	return sandbox || isTomorrowMode
		? patient?.scheduledEndTime <= getCurrentTime(sandbox, isTomorrowMode)
		: patient?.internalStatus === "Completed"
}

export function allowedToEdit(patient, sandbox, isTomorrowMode) {
	return sandbox || isTomorrowMode ? true : !patient?.actualEndTime
}

export function getTimeForTable(patient) {
	return patient?.actualStartTime ? patient?.actualStartTime : patient?.scheduledStartTime
}

export function getOngoingAndFuturePatientsAllCases(patientSchedule, sandbox, isTomorrowMode) {
	return patientSchedule.filter(patient =>
		sandbox || isTomorrowMode
			? currentlyScheduledFuture(patient, sandbox, isTomorrowMode) || currentlyScheduledOngoing(patient, sandbox, isTomorrowMode)
			: currentlyRealTimeFuture(patient, sandbox, isTomorrowMode) || currentlyRealTimeOngoing(patient, sandbox, isTomorrowMode)
	)
}

/**
 * Checks if the user has a specific permission
 * @param {string} permission - The permission to check for (e.g., "write:schedules")
 * @param {Object[]} permissions - Array of permission objects from the user's state
 * @returns {boolean} - Whether the user has the specified permission
 */
export function hasPermission(permissions, permission_to_check) {
	return permissions && permissions.includes(permission_to_check)
}